import { useEffect } from 'react'
import { useLocation } from 'react-router'

export const SCROLL_TO_HASH_TIMEOUT_MS = 5000

const toKebabCase = (value: string) =>
  value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

const decodeHash = (hash: string): string | null => {
  try {
    return decodeURIComponent(hash.replace(/^#/, '')).trim() || null
  } catch {
    return null
  }
}

/** Resolves `#section` to an element by exact id, then by its kebab-case form (`#speedDial` → `speed-dial`). */
export const findHashTarget = (
  hash: string,
  root: Document = document,
): HTMLElement | null => {
  const id = decodeHash(hash)
  if (!id) {
    return null
  }

  return root.getElementById(id) ?? root.getElementById(toKebabCase(id))
}

const isScrollable = (element: HTMLElement) => {
  const { overflowY } = window.getComputedStyle(element)
  return (
    (overflowY === 'auto' || overflowY === 'scroll') &&
    element.scrollHeight > element.clientHeight
  )
}

const findScrollContainer = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement
  while (parent && parent !== document.body) {
    if (isScrollable(parent)) {
      return parent
    }
    parent = parent.parentElement
  }
  return null
}

/** Scrolls only the nearest scrollable ancestor so `overflow: hidden` layout wrappers don't shift. */
export const scrollToElement = (element: HTMLElement) => {
  const container = findScrollContainer(element)
  if (!container) {
    element.scrollIntoView({ block: 'start' })
    return
  }

  const offset =
    element.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop -
    Number.parseFloat(window.getComputedStyle(container).paddingTop || '0')

  container.scrollTo({ top: Math.max(0, offset) })
}

const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6, [role="heading"]'

const hasOwnText = (element: Element) =>
  Array.from(element.childNodes).some(
    (node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
  )

const isExposed = (element: Element) => !element.closest('[aria-hidden="true"]')

/** A section's heading: its first semantic heading, else its first exposed element with its own text. */
export const findSectionHeading = (section: HTMLElement): HTMLElement | null =>
  Array.from(section.querySelectorAll<HTMLElement>(HEADING_SELECTOR)).find(
    isExposed,
  ) ??
  Array.from(section.querySelectorAll<HTMLElement>('*')).find(
    (element) => hasOwnText(element) && isExposed(element),
  ) ??
  null

const setTemporaryAttribute = (
  element: HTMLElement,
  name: string,
  value: string,
) => {
  element.setAttribute(name, value)
  return () => element.removeAttribute(name)
}

/**
 * Names an unnamed section after its heading so screen readers announce the
 * heading on focus instead of reading the whole section.
 */
const labelSectionByHeading = (section: HTMLElement): (() => void)[] => {
  if (
    hasOwnText(section) ||
    section.hasAttribute('aria-label') ||
    section.hasAttribute('aria-labelledby')
  ) {
    return []
  }
  const heading = findSectionHeading(section)
  if (!heading) {
    return []
  }

  const cleanups: (() => void)[] = []
  if (!heading.id) {
    cleanups.push(
      setTemporaryAttribute(heading, 'id', `${section.id}-hash-heading`),
    )
  }
  cleanups.push(setTemporaryAttribute(section, 'aria-labelledby', heading.id))
  if (!section.hasAttribute('role')) {
    cleanups.push(setTemporaryAttribute(section, 'role', 'group'))
  }
  return cleanups
}

/** Set while a hash target holds the focus it was given; `app.global.css` draws its focus ring. */
export const HASH_TARGET_FOCUS_ATTRIBUTE = 'data-hash-target-focus'

/**
 * Moves keyboard focus to a hash target without scrolling again and returns
 * whether focus landed. Non-focusable targets get a temporary `tabindex="-1"`
 * (plus a heading-based name when they have none). Temporary attributes are
 * removed once focus moves elsewhere in the page, but not when the whole
 * window loses focus: removing `tabindex` from the still-active element would
 * drop focus to the body.
 */
export const focusHashTarget = (element: HTMLElement): boolean => {
  const cleanups: (() => void)[] = []
  if (!element.hasAttribute('tabindex') && element.tabIndex < 0) {
    cleanups.push(
      setTemporaryAttribute(element, 'tabindex', '-1'),
      ...labelSectionByHeading(element),
    )
  }
  cleanups.push(setTemporaryAttribute(element, HASH_TARGET_FOCUS_ATTRIBUTE, ''))
  const cleanUp = () => cleanups.forEach((cleanup) => cleanup())

  element.focus({ preventScroll: true })
  if (document.activeElement !== element) {
    cleanUp()
    return false
  }

  const handleBlur = () => {
    if (!document.hasFocus() || document.activeElement === element) {
      return
    }
    element.removeEventListener('blur', handleBlur)
    cleanUp()
  }
  element.addEventListener('blur', handleBlur)
  return true
}

const WATCHED_ATTRIBUTES = ['id', 'class', 'style', 'hidden', 'inert']

/**
 * Scrolls to and focuses the element named by the URL hash once it renders.
 * Until `SCROLL_TO_HASH_TIMEOUT_MS`, a MutationObserver retries when the target
 * mounts late, can't take focus yet, or is re-rendered as a new node after
 * being focused. Focus is never taken back once the user has moved it.
 */
export const useScrollToHash = () => {
  const { hash, pathname, key } = useLocation()

  useEffect(() => {
    if (!hash) {
      return
    }

    const initialActiveElement = document.activeElement
    let scrolledTarget: HTMLElement | null = null
    let focusedTarget: HTMLElement | null = null

    const userMovedFocus = () => {
      const active = document.activeElement
      return (
        !!active &&
        active !== document.body &&
        active !== initialActiveElement &&
        active !== focusedTarget
      )
    }

    /** Returns `true` once there is nothing left to wait for. */
    const settle = () => {
      const target = findHashTarget(hash)
      if (!target || focusedTarget?.isConnected) {
        return false
      }
      if (target !== scrolledTarget) {
        scrollToElement(target)
        scrolledTarget = target
      }
      if (userMovedFocus()) {
        return true
      }
      if (focusHashTarget(target)) {
        focusedTarget = target
      }
      return false
    }

    if (settle()) {
      return
    }

    const observer = new MutationObserver(() => {
      if (settle()) {
        stopWatching()
      }
    })
    const timeoutId = window.setTimeout(
      () => observer.disconnect(),
      SCROLL_TO_HASH_TIMEOUT_MS,
    )
    const stopWatching = () => {
      observer.disconnect()
      window.clearTimeout(timeoutId)
    }

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: WATCHED_ATTRIBUTES,
    })

    return stopWatching
  }, [hash, pathname, key])
}
